import { Helmet } from "react-helmet-async";

/**
 * SEOHead — drop-in per-page SEO component.
 *
 * Handles:
 *  • <title> + meta description
 *  • Open Graph (Facebook / LinkedIn / iMessage)
 *  • Twitter / X Card
 *  • Canonical URL
 *  • Robots directive
 *  • JSON-LD structured data (pass array via `schema` prop)
 *
 * Usage:
 *   <SEOHead
 *     title="Polaris — AI Travel CRM"
 *     description="..."
 *     path="/pricing"
 *     schema={[SoftwareApplicationSchema, FaqSchema]}
 *   />
 */

const SITE = "https://polariscrm.ai";
const SITE_NAME = "Polaris — Travel CRM";
const DEFAULT_IMAGE = `${SITE}/og-default.png`;
const TWITTER_HANDLE = "@polariscrm";

export default function SEOHead({
  title,
  description,
  path = "",
  image = DEFAULT_IMAGE,
  noIndex = false,
  schema = [],
  type = "website",
}) {
  const fullTitle = title
    ? `${title} · Polaris`
    : "Polaris — AI Travel CRM for Craft Advisors";
  const canonical = `${SITE}${path}`;

  return (
    <Helmet>
      {/* ── Core ────────────────────────────────────────── */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noIndex ? (
        <meta name="robots" content="noindex,nofollow" />
      ) : (
        <meta name="robots" content="index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1" />
      )}

      {/* ── Open Graph ──────────────────────────────────── */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_US" />

      {/* ── Twitter / X ─────────────────────────────────── */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:creator" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* ── JSON-LD Structured Data ──────────────────────── */}
      {schema.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s, null, 2)}
        </script>
      ))}
    </Helmet>
  );
}

/* ── Shared Schema blocks ────────────────────────────────── */

export const SITE_URL = SITE;

export const OrganizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Polaris",
  url: SITE,
  logo: `${SITE}/logo192.png`,
  contactPoint: {
    "@type": "ContactPoint",
    email: "hello@polariscrm.ai",
    contactType: "customer support",
    availableLanguage: "English",
  },
  sameAs: [
    "https://twitter.com/polariscrm",
  ],
};

export const SoftwareApplicationSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Polaris",
  alternateName: "Polaris Travel CRM",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  url: SITE,
  description:
    "AI-powered CRM built exclusively for craft travel advisors. Manage clients, trips, pipeline, and flights — with Polaris Copilot drafting itineraries and emails in seconds.",
  offers: [
    {
      "@type": "Offer",
      name: "Starter",
      price: "0",
      priceCurrency: "USD",
      description: "Free forever — up to 5 active trips, 20 AI requests/month",
    },
    {
      "@type": "Offer",
      name: "Pro",
      price: "29",
      priceCurrency: "USD",
      billingIncrement: "P1M",
      description: "Unlimited trips & clients, 300 AI requests/month, full marketing suite",
    },
    {
      "@type": "Offer",
      name: "Agency",
      price: "79",
      priceCurrency: "USD",
      billingIncrement: "P1M",
      description: "Everything in Pro, up to 10 advisor seats, shared client library",
    },
  ],
  featureList: [
    "AI itinerary drafting with Polaris Copilot",
    "Supplier PDF import — auto-creates client + trip",
    "Chrome Extension for VAX, Delta Vacations, Disney and more",
    "Live flight tracking and alerts",
    "Day-by-day trip builder with PDF export",
    "Client relationship management",
    "Stripe payment links and deposit tracking",
    "Email and SMS marketing campaigns",
    "Google OAuth sign-in",
    "Agency team tools and advisor impersonation",
  ],
  screenshot: `${SITE}/og-default.png`,
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.9",
    reviewCount: "48",
    bestRating: "5",
    worstRating: "1",
  },
};

export const WebSiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Polaris Travel CRM",
  url: SITE,
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${SITE}/app/clients?q={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
};

export const LandingFAQSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is Polaris?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Polaris is an AI-powered CRM built exclusively for independent and craft travel advisors. It combines client management, trip building, pipeline tracking, live flight updates, and an AI Copilot — all in one calm workspace.",
      },
    },
    {
      "@type": "Question",
      name: "What does Polaris Copilot do?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Polaris Copilot is an AI assistant built into every trip. You can ask it to draft a day-by-day itinerary, write a personalised client email, import a flight, or even drop a supplier PDF — it reads the booking, creates the client record, and populates the trip automatically.",
      },
    },
    {
      "@type": "Question",
      name: "How does the Chrome Extension work?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "The free Polaris Chrome Extension detects when you're on a supplier booking confirmation page (VAX Vacation Access, Delta Vacations, Disney, Vacation Express, and more) and shows a sidebar with all the reservation details pre-filled. One click imports everything into the right trip in your CRM.",
      },
    },
    {
      "@type": "Question",
      name: "Is Polaris free to use?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes — Polaris has a free Starter plan that includes up to 5 active trips, a full trip builder, live flight tracking, and 20 Polaris Copilot AI requests per month. No credit card required to start.",
      },
    },
    {
      "@type": "Question",
      name: "How much do the paid plans cost?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Pro is $29/month and includes unlimited trips and clients, 300 AI requests/month, Stripe payment links, and the full marketing suite. Agency is $79/seat/month and adds up to 10 advisor seats, a shared client library, and a dedicated agency admin dashboard.",
      },
    },
    {
      "@type": "Question",
      name: "Does Polaris integrate with Google?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. You can sign in and register with Google in one click — no password needed. Polaris also includes built-in hotel and activity search autocomplete inside the trip builder.",
      },
    },
    {
      "@type": "Question",
      name: "Can I import existing clients or bookings?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes — you can import clients via CSV (the AI will clean and map the columns automatically on Pro), and you can import booking confirmations by dropping a supplier PDF into Polaris Copilot or using the Chrome Extension on a supplier's confirmation page.",
      },
    },
  ],
};

export const BreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: item.name,
    item: `${SITE}${item.path}`,
  })),
});
